import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Avatar,
  Button,
  LinearProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { getUser, getUserSkills, User, UserSkill } from '../services/userService';
import { taskService } from '../services/taskService';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const proficiencyColors: Record<string, string> = {
  beginner: '#F59E0B',
  intermediate: '#3B82F6',
  advanced: '#10B981',
  expert: '#8B5CF6',
};

const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  const loadData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [userData, skillsData, tasksData] = await Promise.all([
        getUser(userId),
        getUserSkills(userId).catch(() => []),
        taskService.getTasks({ assigneeId: userId }).catch(() => []),
      ]);
      setUser(userData);
      setSkills(skillsData);
      setTasks(tasksData as any[]);
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  const completedTasks = tasks.filter((t: any) => t.taskStatus?.category === 'done').length;
  const inProgressTasks = tasks.filter((t: any) => t.taskStatus?.category === 'in_progress').length;
  const todoTasks = tasks.filter((t: any) => t.taskStatus?.category === 'todo').length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: 'primary.main' }}>
            {user.firstName?.[0]}{user.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {user.firstName} {user.lastName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Chip icon={<EmailIcon />} label={user.email} size="small" variant="outlined" />
              {user.userLevel && (
                <Chip
                  label={user.userLevel.name}
                  size="small"
                  sx={{ bgcolor: user.userLevel.color || '#6B7280', color: '#fff' }}
                />
              )}
              <Chip
                label={user.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={user.isActive ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Tasks</Typography>
            <Typography variant="h4">{tasks.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Completed</Typography>
            <Typography variant="h4" color="success.main">{completedTasks}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">In Progress</Typography>
            <Typography variant="h4" color="info.main">{inProgressTasks}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Skills</Typography>
            <Typography variant="h4">{skills.length}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Overview" />
          <Tab icon={<AssignmentIcon />} iconPosition="start" label="Tasks" />
          <Tab icon={<StarIcon />} iconPosition="start" label="Skills" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Full Name</Typography>
                <Typography variant="body1">{user.firstName} {user.lastName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Level</Typography>
                <Typography variant="body1">{user.userLevel?.name || 'Not set'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Language</Typography>
                <Typography variant="body1">{user.preferredLanguage || 'Not set'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Timezone</Typography>
                <Typography variant="body1">{user.timeZone || 'Not set'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Joined</Typography>
                <Typography variant="body1">
                  {user.createdAt ? format(new Date(user.createdAt), 'MMM dd, yyyy') : 'Unknown'}
                </Typography>
              </Grid>
              {user.lastLoginAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Last Login</Typography>
                  <Typography variant="body1">
                    {format(new Date(user.lastLoginAt), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Assigned Tasks ({tasks.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task: any) => (
                    <TableRow
                      key={task.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <TableCell>{task.title}</TableCell>
                      <TableCell>
                        <Chip label={task.projectName || task.projectKey || '-'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.taskStatus?.name || 'Unknown'}
                          size="small"
                          sx={{
                            bgcolor: task.taskStatus?.color || '#6B7280',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const p = String(task.priority);
                          const labels: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' };
                          const colors: Record<string, string> = { '1': '#10B981', '2': '#3B82F6', '3': '#F59E0B', '4': '#EF4444' };
                          return <Chip label={labels[p] || p} size="small" sx={{ bgcolor: colors[p] || '#6B7280', color: '#fff' }} />;
                        })()}
                      </TableCell>
                      <TableCell>
                        {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {tasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>No tasks assigned</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Skills Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Skills ({skills.length})
            </Typography>
            <List>
              {skills.map((userSkill) => (
                <React.Fragment key={userSkill.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: proficiencyColors[userSkill.proficiencyLevel?.toLowerCase()] || '#6B7280' }}>
                        <StarIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={userSkill.skill?.name || 'Unknown Skill'}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Chip
                            label={userSkill.proficiencyLevel}
                            size="small"
                            sx={{
                              bgcolor: proficiencyColors[userSkill.proficiencyLevel?.toLowerCase()] || '#6B7280',
                              color: '#fff',
                            }}
                          />
                          {userSkill.yearsOfExperience != null && (
                            <Typography variant="caption" color="text.secondary">
                              {userSkill.yearsOfExperience} years experience
                            </Typography>
                          )}
                          {userSkill.skill?.category && (
                            <Chip label={userSkill.skill.category} size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {skills.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No skills assigned"
                    secondary="Skills can be managed from the Users page"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UserDetailsPage;
